// HypothesisDetail.jsx — Hypothesis detail panel
// Extracted from HypothesisExplorer for modularity.
// Contains: StatsStrip, Regimes, Formal Statement, Main Signal Chart,
// Falsification, Causal Chain, Sensors, Positions, Dependencies,
// Evidence, Literature, Disclaimer.
//
// Usage: <HypothesisDetail detail={detail} chartRange={chartRange}
//           setChartRange={setChartRange} list={list} switchTo={switchTo} />

import { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";
import CausalChainGraph from "./CausalChainGraph";
import SensorPanel from "./SensorPanel";

// ── Shared constants (must match HypothesisExplorer) ──────────────────────
const GENOMA_UI = "";
const MAGMA_ASSET = "https://relatorioreservado.com.br/magma/asset/stock";

const C = {
  bg: "#07070a", card: "#111118", border: "#1e1e2a",
  gold: "#c9a227", red: "#f03e3e", green: "#34d399", blue: "#60a5fa",
  amber: "#fbbf24", purple: "#c084fc", cyan: "#22d3ee", orange: "#fb923c",
  white: "#e8e8f0", dim: "rgba(232,232,240,0.80)", dimmer: "rgba(232,232,240,0.62)",
};

const REGIME_META = {
  EQUILIBRIUM: { color: C.green, dot: "●" },
  INFLATIONARY_STRESS: { color: C.orange, dot: "◆" },
  GEOPOLITICAL_ESCALATION: { color: C.red, dot: "◆" },
  STATE_DEGRADATION: { color: C.amber, dot: "▲" },
  GRID_SECESSION: { color: C.cyan, dot: "▲" },
  CREDIT_IMPLOSION: { color: C.purple, dot: "●" },
  SUPPLY_DISRUPTION: { color: C.blue, dot: "▲" },
  ALL: { color: C.purple, dot: "◈" },
};

const KILL_LEVEL = {
  primary: "MORTE PRIMÁRIA", secondary: "MORTE SECUNDÁRIA", tertiary: "MORTE TERCIÁRIA",
  "primary-long": "MORTE LONG", "primary-short": "MORTE SHORT", meta: "META-FALSIFICAÇÃO",
};

function shortMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  return `${months[parseInt(mo)-1]}/${y.slice(2)}`;
}
function fmtValue(v, decimals = 2) {
  if (v == null || isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(decimals)}`;
}
function fmtRatio(v) {
  if (v == null || isNaN(v)) return "—";
  return v.toFixed(2);
}

// ── Tooltip ───────────────────────────────────────────────────────────────
const MainChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(7,7,10,0.96)", border: "1px solid #2a2a38", borderRadius: 6, padding: "12px 16px", fontFamily: "'IBM Plex Mono', monospace" }}>
      <div style={{ fontSize: 12, color: C.dimmer, marginBottom: 4 }}>{shortMonth(label)}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.white }}>{fmtRatio(payload[0].value)}</div>
    </div>
  );
};

// ── Stats Strip ───────────────────────────────────────────────────────────
function StatsStrip({ detail }) {
  const liveSensors = detail.sensors.filter(s => s.reading?.status === "LIVE").length;
  const totalSensors = detail.sensors.length;
  const firingSensors = detail.sensors.filter(s => s.reading?.signal === "OVER").length;
  const regimeCount = detail.regimes.length;
  const isLive = detail.status === "done";

  const stats = [
    {
      label: "SENSORES",
      value: `${liveSensors}/${totalSensors}`,
      sub: liveSensors === totalSensors ? "todos live" : `${totalSensors - liveSensors} pendente${totalSensors - liveSensors > 1 ? "s" : ""}`,
      color: liveSensors === totalSensors ? C.green : C.amber,
    },
    {
      label: "REGIMES",
      value: regimeCount,
      sub: regimeCount === 1 ? "regime ativo" : "regimes ativos",
      color: detail.color,
    },
    {
      label: "SINAL",
      value: firingSensors > 0 ? "FIRING" : isLive ? "NOMINAL" : "STANDBY",
      sub: firingSensors > 0 ? `${firingSensors} sensor${firingSensors > 1 ? "es" : ""} em alerta` : isLive ? "dentro dos limiares" : "aguardando pipeline",
      color: firingSensors > 0 ? C.red : isLive ? C.green : C.dimmer,
    },
    {
      label: "CADEIA",
      value: `${detail.causal_chain.length} est.`,
      sub: detail.causal_chain.length > 0 ? `lag máx · ${detail.causal_chain[detail.causal_chain.length - 1]?.lag}` : "—",
      color: C.blue,
    },
  ];

  return (
    <div className="rr-hyp-stats-strip" style={{
      display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
      gap: 1, margin: "0 26px", background: "#1e1e2a", borderRadius: 6, overflow: "hidden",
    }}>
      {stats.map((s, i) => (
        <div key={i} style={{ padding: "16px 18px", background: C.bg, textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.dimmer, letterSpacing: "0.14em", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace" }}>{s.label}</div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
          <div style={{ fontSize: 10, color: C.dimmer, marginTop: 6, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.03em" }}>{s.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── Range tabs ────────────────────────────────────────────────────────────
function RangeTabs({ active, onChange, color }) {
  const opts = [{ key: "12M", label: "12M" }, { key: "5A", label: "5A" }, { key: "ALL", label: "TUDO" }];
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {opts.map(o => (
        <button key={o.key} onClick={() => onChange(o.key)} style={{
          padding: "5px 12px", borderRadius: 3,
          border: active === o.key ? `1px solid ${color}60` : "1px solid transparent",
          background: active === o.key ? `${color}14` : "transparent",
          color: active === o.key ? color : C.dimmer,
          fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
          fontFamily: "'IBM Plex Mono', monospace",
          cursor: "pointer", transition: "all 0.15s",
        }}>{o.label}</button>
      ))}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN DETAIL COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function HypothesisDetail({ detail, chartRange, setChartRange, list, switchTo }) {
  const isLive = detail.status === "done";
  const main = detail.main_signal;

  const filteredSeries = useMemo(() => {
    if (!main?.series) return [];
    const s = main.series;
    if (chartRange === "ALL") return s;
    if (chartRange === "12M") return s.slice(-12);
    if (chartRange === "5A") return s.slice(-60);
    return s;
  }, [main?.series, chartRange]);

  return (
    <>
      {/* ── Stats Strip ──────────────────────────────────────────── */}
      <div style={{ padding: "18px 0 0" }}>
        <StatsStrip detail={detail} />
      </div>

      {/* ── Regimes ──────────────────────────────────────────────── */}
      <div style={{ padding: "22px 26px 6px", position: "relative" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.dimmer, letterSpacing: "0.14em", marginBottom: 10 }}>ATIVA NOS REGIMES</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {detail.regimes.map(r => {
            const m = REGIME_META[r.code] || { color: C.dim, dot: "●" };
            return (
              <span key={r.code} style={{
                padding: "6px 12px", borderRadius: 4,
                background: `${m.color}10`, border: `1px solid ${m.color}30`,
                color: m.color, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
              }}>
                {m.dot} <span style={{ marginLeft: 4 }}>{r.code}</span>
                <span style={{ opacity: 0.75, marginLeft: 7, fontWeight: 500 }}>{r.label}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* ── Formal Statement ─────────────────────────────────────── */}
      <div style={{ padding: "22px 26px", position: "relative" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: detail.color, letterSpacing: "0.14em", marginBottom: 10 }}>ESPECIFICAÇÃO FORMAL</div>
        <p style={{
          fontFamily: "'Fraunces', Georgia, serif", fontSize: 17,
          color: C.dim, lineHeight: 1.55, margin: 0, letterSpacing: "0.005em",
          paddingLeft: 14, borderLeft: `3px solid ${detail.color}55`,
        }}>{detail.formal_statement}</p>
      </div>

      {/* ── Main Signal Chart ────────────────────────────────────── */}
      {main && main.series && main.series.length > 0 && (
        <div style={{
          margin: "0 26px", padding: "22px 22px 18px",
          background: "rgba(255,255,255,0.02)",
          border: `1px solid ${detail.color}30`, borderRadius: 8,
          position: "relative",
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 16,
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: detail.color, letterSpacing: "0.14em" }}>SINAL PRINCIPAL · LIVE DO GENOMA</div>
                <RangeTabs active={chartRange} onChange={setChartRange} color={detail.color} />
              </div>
              <div style={{
                fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700,
                color: C.white, letterSpacing: "-0.01em", marginBottom: 4,
              }}>{main.name}</div>
              <p style={{
                fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic",
                fontSize: 13, color: C.dim, margin: 0, lineHeight: 1.45, maxWidth: 480,
              }}>{main.description}</p>
            </div>
            <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
              <div style={{
                padding: "12px 16px", borderRadius: 5,
                background: "rgba(255,255,255,0.03)", border: "1px solid #1e1e2a",
                textAlign: "right",
              }}>
                <div style={{ fontSize: 10, color: C.dimmer, letterSpacing: "0.14em", marginBottom: 4 }}>ATUAL</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 26, fontWeight: 700, color: C.white }}>{fmtRatio(main.current)}</div>
              </div>
              <div style={{
                padding: "12px 16px", borderRadius: 5,
                background: main.direction === "falling" ? "rgba(240,62,62,0.06)" : main.direction === "rising" ? "rgba(52,211,153,0.06)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${main.direction === "falling" ? "rgba(240,62,62,0.28)" : main.direction === "rising" ? "rgba(52,211,153,0.28)" : "#1e1e2a"}`,
                textAlign: "right",
              }}>
                <div style={{ fontSize: 10, color: C.dimmer, letterSpacing: "0.14em", marginBottom: 4 }}>Δ 12mo</div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 26, fontWeight: 700,
                  color: main.direction === "falling" ? C.red : main.direction === "rising" ? C.green : C.white,
                }}>{fmtValue(main.delta_12mo, 1)}%</div>
              </div>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={filteredSeries} margin={{ top: 6, right: 20, left: 8, bottom: 4 }}>
              <defs>
                <linearGradient id="rr-main-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={detail.color} stopOpacity={0.40} />
                  <stop offset="100%" stopColor={detail.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
              <XAxis dataKey="month" fontSize={10} tickLine={false} axisLine={false}
                tickFormatter={shortMonth} interval={Math.max(1, Math.floor(filteredSeries.length / 8))}
                tick={{ fill: "rgba(232,232,240,0.25)", fontFamily: "'IBM Plex Mono', monospace" }} />
              <YAxis fontSize={10} tickLine={false} axisLine={false}
                tickFormatter={v => v.toFixed(1)} width={36}
                tick={{ fill: "rgba(232,232,240,0.25)", fontFamily: "'IBM Plex Mono', monospace" }} />
              <Tooltip content={<MainChartTooltip />} />
              {main.threshold != null && (
                <ReferenceLine y={parseFloat(main.threshold)} stroke={C.red} strokeDasharray="4 3" strokeWidth={1.2} strokeOpacity={0.6}
                  label={{ value: `limiar ${main.threshold}`, position: "right", fill: C.red, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} />
              )}
              <Area type="monotone" dataKey="value" stroke={detail.color} strokeWidth={2.5}
                fill="url(#rr-main-grad)" dot={false}
                activeDot={{ r: 5, fill: detail.color, stroke: "#000", strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Falsification Criteria ───────────────────────────────── */}
      <div style={{
        margin: "20px 26px", padding: "20px 22px",
        background: "rgba(240,62,62,0.06)", border: "1px solid rgba(240,62,62,0.18)", borderRadius: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.red, letterSpacing: "0.14em" }}>✕ CRITÉRIOS DE FALSIFICAÇÃO</span>
          <span style={{ fontSize: 13, color: C.dim, fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic" }}>o que mata a hipótese</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {detail.falsification.map((f, i) => (
            <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <span style={{
                flexShrink: 0, fontSize: 10, fontWeight: 700, color: C.red,
                letterSpacing: "0.1em", padding: "5px 10px", borderRadius: 3,
                background: "rgba(240,62,62,0.10)", border: "1px solid rgba(240,62,62,0.28)",
                minWidth: 130, textAlign: "center",
              }}>{KILL_LEVEL[f.level] || f.level.toUpperCase()}</span>
              <p style={{
                fontSize: 13, color: C.dim, lineHeight: 1.55, margin: 0,
                fontFamily: "'IBM Plex Mono', monospace",
              }}>{f.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Causal Chain ─────────────────────────────────────────── */}
      <div style={{ padding: "22px 26px 12px", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: detail.color, letterSpacing: "0.14em" }}>CADEIA CAUSAL · {detail.causal_chain.length} ESTÁGIOS</div>
        </div>
        <p style={{
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", fontSize: 13,
          color: C.dim, margin: "0 0 16px",
        }}>Cada estágio é observável através de fonte de dados distinta. O sinal propaga da esquerda para a direita com lag acumulativo.</p>
        <CausalChainGraph chain={detail.causal_chain} color={detail.color} />
      </div>

      {/* ── Sensors ──────────────────────────────────────────────── */}
      <SensorPanel sensors={detail.sensors} color={detail.color} isLive={isLive} genomaUI={GENOMA_UI} />

      {/* ── Position Structure ────────────────────────────────────── */}
      <div style={{ padding: "22px 26px 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: detail.color, letterSpacing: "0.14em", marginBottom: 6 }}>ESTRUTURA DE POSIÇÃO</div>
        <p style={{
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", fontSize: 13,
          color: C.dim, margin: "0 0 16px",
        }}>Long timing à cascata prevista, short imediato ao gatilho detectado. Equal-weight dentro de cada perna.</p>
        <div className="rr-hyp-split" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
          {[
            { side: "long", data: detail.positions.long, label: "▲ LONG", color: C.green, bg: "rgba(52,211,153,0.05)", border: "rgba(52,211,153,0.22)" },
            { side: "short", data: detail.positions.short, label: "▼ SHORT", color: C.red, bg: "rgba(240,62,62,0.05)", border: "rgba(240,62,62,0.22)" },
          ].map(({ side, data, label, color, bg, border }) => (
            <div key={side} style={{ padding: "18px 20px", background: bg, border: `1px solid ${border}`, borderRadius: 6 }}>
              <span style={{
                display: "inline-block", marginBottom: 12, padding: "5px 12px", borderRadius: 3,
                background: `${color}20`, border: `1px solid ${color}40`,
                color, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
              }}>{label}</span>
              <p style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
                color: C.dim, lineHeight: 1.55, margin: "0 0 14px",
              }}>{data.thesis}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {data.tickers.map(t => (
                  t.includes(" ") || t.length > 8 ? (
                    <span key={t} style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                      color: C.dimmer, fontStyle: "italic", padding: "5px 10px",
                    }}>{t}</span>
                  ) : (
                    <a key={t} href={`${MAGMA_ASSET}/${encodeURIComponent(t)}`} target="_blank" rel="noopener noreferrer" className="rr-hyp-ticker" style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                      fontWeight: 700, color, padding: "5px 12px", borderRadius: 3,
                      background: `${color}12`, border: `1px solid ${color}30`,
                      textDecoration: "none", letterSpacing: "0.04em", transition: "all 0.15s",
                    }}>{t}</a>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Dependency Graph ──────────────────────────────────────── */}
      <div style={{ padding: "22px 26px 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: detail.color, letterSpacing: "0.14em", marginBottom: 10 }}>GRAFO DE DEPENDÊNCIAS</div>
        <div className="rr-hyp-split" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
          {[
            { label: "ALIMENTADA POR", data: detail.dependencies.fed_by, tone: "muted" },
            { label: "ALIMENTA", data: detail.dependencies.feeds, tone: "active" },
          ].map((d, di) => (
            <div key={di} style={{
              padding: "16px 18px", background: "rgba(255,255,255,0.02)",
              border: "1px solid #1e1e2a", borderRadius: 6,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.dimmer, letterSpacing: "0.14em", marginBottom: 10 }}>{d.label}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {d.data.map((r, i) => {
                  const exists = list.some(h => h.id === r.id);
                  const Tag = exists ? "button" : "span";
                  return (
                    <Tag key={i}
                      className={exists ? "rr-hyp-xref" : ""}
                      onClick={exists ? () => switchTo(r.id) : undefined}
                      style={{
                        padding: "5px 10px", borderRadius: 3,
                        background: d.tone === "active" ? `${detail.color}12` : "rgba(255,255,255,0.04)",
                        border: d.tone === "active" ? `1px solid ${detail.color}28` : "1px solid #1e1e2a",
                        color: d.tone === "active" ? detail.color : C.dim,
                        fontSize: 12, fontWeight: 500,
                        cursor: exists ? "pointer" : "default",
                        fontFamily: "'IBM Plex Mono', monospace",
                        transition: "all 0.15s",
                      }}>
                      <span style={{ fontWeight: 700 }}>{r.id}</span>
                      <span style={{ opacity: 0.78, marginLeft: 6 }}>{r.name}</span>
                      {exists && <span style={{ marginLeft: 6, opacity: 0.5 }}>↗</span>}
                    </Tag>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Empirical Evidence ────────────────────────────────────── */}
      <div style={{ padding: "22px 26px 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: detail.color, letterSpacing: "0.14em", marginBottom: 10 }}>EVIDÊNCIA EMPÍRICA</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {detail.evidence.map((e, i) => (
            <div key={i} style={{
              padding: "14px 18px",
              background: "rgba(255,255,255,0.02)",
              borderLeft: `3px solid ${detail.color}55`, borderRadius: 3,
            }}>
              <div style={{
                fontFamily: "'Fraunces', Georgia, serif", fontSize: 15,
                fontWeight: 700, color: C.white, letterSpacing: "-0.005em", marginBottom: 6,
              }}>{e.case}</div>
              <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.55 }}>{e.note}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Literature ────────────────────────────────────────────── */}
      <div style={{ padding: "14px 26px 20px" }}>
        <div style={{
          padding: "14px 16px",
          background: "rgba(201,162,39,0.05)", border: "1px solid rgba(201,162,39,0.15)", borderRadius: 5,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.14em", marginBottom: 6 }}>FUNDAÇÃO NA LITERATURA</div>
          <div style={{
            fontSize: 12, color: C.dim, lineHeight: 1.55,
            fontFamily: "'IBM Plex Mono', monospace",
          }}>{detail.citation}</div>
        </div>
      </div>

      {/* ── Disclaimer ────────────────────────────────────────────── */}
      <div style={{ margin: "0 26px 22px", padding: "12px 16px", background: "rgba(240,62,62,0.05)", border: "1px solid rgba(240,62,62,0.15)", borderRadius: 4 }}>
        <p style={{
          fontSize: 12, color: "rgba(240,62,62,0.80)", lineHeight: 1.5, margin: 0,
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic",
        }}>
          <strong style={{ color: C.red, fontStyle: "normal", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.1em", fontSize: 11 }}>✕ AVISO ANTIFRAGILIDADE ·</strong>{" "}
          Esta é uma hipótese falsificável, não uma tese defendida. Se o dado disser não, a matamos. Não constitui recomendação de investimento.
        </p>
      </div>
    </>
  );
}
